import { z } from 'zod';

import { base64, seal, unbase64, unseal } from './crypto';

const envelopeSchema = z.object({
  format: z.literal('cairn-vault-export'),
  version: z.literal(1),
  kdf: z.object({
    name: z.literal('PBKDF2'),
    hash: z.literal('SHA-256'),
    iterations: z.literal(600000),
    salt: z.string().max(32),
  }),
  encrypted: z.object({ version: z.literal(1), iv: z.string().max(24), data: z.string() }),
});

async function exportKey(password: string, salt: Uint8Array<ArrayBuffer>) {
  const material = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(password),
    'PBKDF2',
    false,
    ['deriveKey'],
  );

  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations: 600000 },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function encryptVaultExport(value: unknown, password: string) {
  if (password.length < 12) {
    throw new Error('Use an export password of at least 12 characters.');
  }

  const salt = crypto.getRandomValues(new Uint8Array(16));

  return {
    format: 'cairn-vault-export',
    version: 1,
    kdf: { name: 'PBKDF2', hash: 'SHA-256', iterations: 600000, salt: base64(salt) },
    encrypted: await seal(value, await exportKey(password, salt), 'cairn-vault-export-v1'),
  };
}

export async function decryptVaultExport<T = unknown>(
  input: unknown,
  password: string,
): Promise<T> {
  const envelope = envelopeSchema.parse(input),
    salt = unbase64(envelope.kdf.salt);

  if (salt.length !== 16 || unbase64(envelope.encrypted.iv).length !== 12) {
    throw new Error('Invalid encrypted export.');
  }

  return unseal<T>(envelope.encrypted, await exportKey(password, salt), 'cairn-vault-export-v1');
}
